import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Shield, Zap, CheckCircle, Star, MapPin, ChevronRight } from 'lucide-react';
import { apiGet, type Game, type MapSection } from '../lib/api';
import { demoGames, demoSectionAvailability, useDemoMode } from '../lib/demo';

type GameStats = { minPriceCents: number; listingCount: number };

export default function IndexPage() {
  const [demoOn] = useDemoMode();
  const [games, setGames] = useState<Game[]>([]);
  const [stats, setStats] = useState<Record<string, GameStats>>({});
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    apiGet<{ games: Game[] }>('/games')
      .catch(() => ({ games: [] as Game[] }))
      .then(({ games }) => {
        if (cancelled) return;
        setGames(games.length ? games : demoOn ? demoGames() : []);
      });
    return () => {
      cancelled = true;
    };
  }, [demoOn]);

  useEffect(() => {
    let cancelled = false;
    const wanted = games.slice(0, 9);
    Promise.all(
      wanted.map(async (g) => {
        const { sections } = await apiGet<{ sections: MapSection[] }>(
          `/map/availability?game_id=${g.game_id}`
        ).catch(() => ({ sections: [] as MapSection[] }));
        const merged = demoOn
          ? [
              ...sections,
              ...demoSectionAvailability(g.game_id).filter(
                (d) => !sections.some((s) => s.section.trim().toUpperCase() === d.section)
              ),
            ]
          : sections;
        if (!merged.length) return null;
        return [
          g.game_id,
          {
            minPriceCents: Math.min(...merged.map((s) => s.min_price_cents)),
            listingCount: merged.reduce((n, s) => n + s.listing_count, 0),
          },
        ] as const;
      })
    ).then((entries) => {
      if (cancelled) return;
      setStats(Object.fromEntries(entries.filter(Boolean) as [string, GameStats][]));
    });
    return () => {
      cancelled = true;
    };
  }, [games, demoOn]);

  function search() {
    const q = query.trim();
    navigate(q ? `/list?section=${encodeURIComponent(q)}` : '/list');
  }

  return (
    <section>
      {/* Hero */}
      <div className="bg-giants-blue rounded-2xl text-white px-6 py-10 sm:px-10">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl !mb-3">
          NY Giants
          <br />
          <span className="text-[#d64559]">Season Ticket</span> Resale
        </h1>
        <p className="text-white/70 text-base sm:text-lg max-w-xl m-0">
          Verified peer-to-peer resale. Every seat confirmed. Every payout escrowed.
        </p>

        <div className="flex gap-2 max-w-2xl mt-6">
          <div className="flex-1 flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 focus-within:border-white/60 transition-colors">
            <Search size={16} className="text-white/50 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="Search by section — e.g. 113 or 224A…"
              className="!border-0 !bg-transparent !text-white placeholder:text-white/40 text-sm !outline-none !p-2.5"
            />
          </div>
          <button onClick={search} className="btn-urgent px-5 shrink-0">
            Search
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
          {[
            { icon: <Shield size={14} />, label: 'Verified sellers only' },
            { icon: <Zap size={14} />, label: 'Instant digital transfer' },
            { icon: <CheckCircle size={14} />, label: 'Escrow buyer protection' },
            { icon: <Star size={14} />, label: 'Season ticket holders' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-1.5 text-white/60 text-xs font-medium">
              <span className="text-giants-gray">{item.icon}</span>
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Schedule */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="!m-0">Season schedule</h2>
          <span className="text-sm muted">
            {games.length ? `${games.length} home game${games.length === 1 ? '' : 's'}` : ''}
          </span>
        </div>
        {games.length === 0 && (
          <p className="muted">
            No games scheduled yet. Turn on "Demo data" in the top bar to explore with sample
            games and listings.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.map((g) => (
            <GameCard key={g.game_id} game={g} stats={stats[g.game_id]} />
          ))}
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10">
        <Link to="/map" className="card card-hover">
          <h3>
            <MapPin size={16} className="inline mr-1.5 -mt-0.5" />
            Stadium map
          </h3>
          <p className="muted m-0">Pick your section visually — every MetLife section, drill down for listings.</p>
        </Link>
        <Link to="/seller/apply" className="card card-hover">
          <h3>
            <Star size={16} className="inline mr-1.5 -mt-0.5" />
            Sell your seats
          </h3>
          <p className="muted m-0">Season ticket holder? Apply as a verified seller and list in minutes.</p>
        </Link>
      </div>

      {/* Trust banner */}
      <div className="bg-giants-blue rounded-2xl mt-10 px-6 py-8 sm:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center sm:text-left">
          {[
            {
              icon: <Shield size={24} />,
              title: 'Verified Sellers',
              body: 'Every listing is tied to a confirmed season ticket holder account. No resellers, no bots.',
            },
            {
              icon: <Zap size={24} />,
              title: 'Instant Transfer',
              body: 'Tickets are delivered digitally via NFL ticket transfer as soon as payment clears.',
            },
            {
              icon: <CheckCircle size={24} />,
              title: 'Escrow Protection',
              body: 'Your payment is held until you confirm receipt. If transfer fails, you get a full refund.',
            },
          ].map((item) => (
            <div key={item.title} className="flex flex-col sm:flex-row items-center sm:items-start gap-3">
              <div className="text-giants-gray shrink-0">{item.icon}</div>
              <div>
                <h3 className="text-white !mb-1 text-base">{item.title}</h3>
                <p className="text-white/60 text-sm m-0">{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GameCard({ game, stats }: { game: Game; stats?: GameStats }) {
  return (
    <Link
      to={`/list?game_id=${encodeURIComponent(game.game_id)}`}
      className="card card-hover group"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs font-semibold muted uppercase tracking-wide m-0">
            {game.week_number ? `Week ${game.week_number}` : game.season}
          </p>
          <h3 className="!mb-0 mt-0.5 text-base">vs. {game.opponent}</h3>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-sm muted mb-4">
        <MapPin size={13} />
        <span>
          {game.game_date}
          {game.kickoff_time ? ` · ${game.kickoff_time}` : ''}
        </span>
      </div>
      <div className="flex items-end justify-between">
        <div>
          {stats ? (
            <>
              <p className="text-[11px] muted m-0">from</p>
              <p className="text-xl font-bold text-giants-blue tabular-nums m-0">
                ${Math.round(stats.minPriceCents / 100)}
              </p>
            </>
          ) : (
            <p className="text-sm muted m-0">No listings yet</p>
          )}
        </div>
        <div className="text-right">
          {stats && (
            <p className="text-[11px] muted m-0">
              {stats.listingCount} listing{stats.listingCount === 1 ? '' : 's'}
            </p>
          )}
          <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-giants-blue group-hover:text-giants-red transition-colors">
            Browse <ChevronRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}
